import { useContext } from 'react';

import { cn } from '@veraclins-dev/utils';

import {
  type CustomComponent,
  type OverrideComponentProps,
  type WithTooltip,
} from '../types';

import { LinkDefaultsContext } from './contexts/link-context';
import { type LinkVariants, linkVariants } from './utils/variants/link';
import { extractStyleProps } from './utils/variants/styles';
import { ComposedTooltip } from './tooltip';

type Target = '_blank' | '_self' | '_parent' | '_top';

// Base props for the Link component
interface BaseLinkProps extends LinkVariants {
  className?: string;
  children?: React.ReactNode;
  target?: Target;
  rel?: string;
}

// Define a type for custom components

type LinkProps<RootComponent extends 'a' | CustomComponent = 'a'> =
  OverrideComponentProps<RootComponent, BaseLinkProps>;

/**
 * A navigation link with variant styles that adds safe `rel` values for external targets.
 * Inside an `Alert`, a `type="link"` link defaults to `tone="inherit"` and
 * `underline="always"`; a `tone` or `underline` set on the link wins.
 * A pressed link gives feedback while the pointer or finger is down: an inline
 * link dims and a `type="button"` link scales down like `Button`.
 * @category interactive-elements
 */
function Link<C extends 'a' | CustomComponent = 'a'>({
  tooltip,
  component = 'a' as C,
  type,
  emphasis,
  tone,
  underline,
  linkSize,
  className,
  children,
  target,
  rel,
  ...props
}: WithTooltip<LinkProps<C>>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = component as React.ComponentType<any>;
  // Surface defaults style inline links only; a button-styled link keeps its
  // own tone so it still gets a surface.
  const surfaceDefaults = useContext(LinkDefaultsContext);
  const defaults = (type ?? 'link') === 'link' ? surfaceDefaults : {};
  const { styleProps, others } = extractStyleProps(props);

  // Ensure rel includes 'noopener noreferrer' for external links when target is '_blank'
  const computedRel =
    target === '_blank'
      ? (() => {
          const relValues = (rel || '').split(' ').filter(Boolean);
          if (!relValues.includes('noopener')) relValues.push('noopener');
          if (!relValues.includes('noreferrer')) relValues.push('noreferrer');
          return relValues.join(' ').trim();
        })()
      : rel;

  const classes = cn(
    linkVariants({
      type,
      emphasis,
      tone: tone ?? defaults.tone,
      underline: underline ?? defaults.underline,
      linkSize,
      ...styleProps,
      className,
    }),
  );

  return tooltip ? (
    <ComposedTooltip
      Trigger={Component}
      TriggerProps={{
        ...others,
        className: classes,
        target,
        rel: computedRel,
        children,
        ...others,
      }}
      content={tooltip}
    />
  ) : (
    <Component
      data-slot="link"
      className={classes}
      target={target}
      rel={computedRel}
      {...others}
    >
      {children}
    </Component>
  );
}

export { Link, type LinkProps, linkVariants };
